import { FC, useState } from "react";

import { SortingDirection } from "@app/shared/lib/enums/sorting-direction";
import { useSdk } from "@app/shared/lib/hooks";
import { DateTime } from "@app/shared/ui/DateTime";
import { HelpText } from "@app/shared/ui/HelpText";
import { Loc } from "@app/shared/ui/Loc";
import { Price } from "@app/shared/ui/Price";
import { Table } from "@app/shared/ui/Table";
import { TruncatedText } from "@app/shared/ui/TruncatedText";

export const BonusTable: FC = () => {
  const [isEmpty, setEmpty] = useState(false);
  const sdk = useSdk();

  return isEmpty ? (
    <HelpText
      label={
        <Loc
          word="Contact.bonus.empty"
          default="No bonus transactions available"
        />
      }
      className="fs-14"
    />
  ) : (
    <Table
      defaultFilters={{ take: 25 }}
      fetchItemsByFilter={async (filters) => {
        const res = await sdk.account.bonus.fetch({
          ...filters,
          sortType: filters.sortType === SortingDirection.asc ? "Asc" : "Desc",
        });

        if (res.body.items.length === 0) {
          setEmpty(true);
        }

        return {
          items: res.body.items,
          filters: {
            ...res.body.filter,
            sortType:
              res.body.filter.sortType === "Desc"
                ? SortingDirection.desc
                : SortingDirection.asc,
            pageTotal: sdk.account.bonus.filter.pageTotal,
            page: sdk.account.bonus.filter.page,
          },
        };
      }}
      columns={[
        {
          title: <Loc word="Global.date" default="Date" />,
          key: "date",
          dataIndex: "CreatedDate",
          sorter: true,
          width: "25%",
          render: (_text, item) => (
            <div>
              <DateTime date={item.createdDate} />
              {item.validUntilDate && (
                <div className="text-placeholder fs-12">
                  <Loc word="Global.until" default="until" />
                  &nbsp;
                  <DateTime date={item.validUntilDate} />
                </div>
              )}
            </div>
          ),
        },
        {
          title: <Loc word="Global.info" default="Info" />,
          key: "info",
          width: "25%",
          render: (_text, item) => (
            <div className="lh-18 fs-12">
              {item.comment && (
                <div>
                  <Loc word="Global.comment" />
                  :&nbsp;
                  <TruncatedText maxLines={2} className="text-placeholder">
                    {item.comment}
                  </TruncatedText>
                </div>
              )}
              {item.transaction && (
                <Loc
                  word="Partnership.transaction.transferFromPartnership"
                  default="Transfer from partnership account"
                  className="text-placeholder"
                />
              )}
              {item.order && (
                <span className="text-placeholder">
                  <Loc word="PurchaseOrder" default="Order" />
                  &nbsp; #{item.order.number}
                </span>
              )}
            </div>
          ),
        },
        {
          title: <Loc word="Contact.bonus.amount" default="Amount" />,
          key: "amount",
          dataIndex: "amount",
          sorter: true,
          width: "25%",
          align: "right",
          render: (_text, item) => (
            <Price price={item.amount} truncateIfFractionalZero />
          ),
        },
      ]}
      hidePaginationOnePage
      size="small"
      style={{ maxWidth: 1280 }}
    />
  );
};
